/**
 * Utility functions for parsing and formatting message content
 * Enhanced with markdown parsing capabilities, improved structure, and string safety
 */

/**
 * Safely ensures a value is a string
 * @param {any} value - The value to convert to string
 * @returns {string} The value as a string or empty string if null/undefined
 */
const ensureString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return String(value);
  } catch (e) {
    return '';
  }
};

/**
 * Safely checks if a string includes a substring
 * @param {any} str - The string to check
 * @param {any} searchValue - The substring to search for
 * @returns {boolean} True if the string includes the substring, false otherwise
 */
const safeIncludes = (str, searchValue) => {
  const safeStr = ensureString(str);
  const safeSearchValue = ensureString(searchValue);
  if (!safeStr || !safeSearchValue) return false;
  return safeStr.includes(safeSearchValue);
};

/**
 * Safely matches a string against a regex
 * @param {any} str - The string to match
 * @param {RegExp} regex - The regex to match against
 * @returns {RegExpMatchArray|null} The match result or null
 */
const safeMatch = (str, regex) => {
  const safeStr = ensureString(str);
  if (!safeStr || !(regex instanceof RegExp)) return null;
  return safeStr.match(regex);
};

/**
 * Helper function to detect content type based on message text
 * @param {any} text - The message text to analyze
 * @returns {string} The detected content type
 */
export const detectContentType = (text) => {
  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return 'general';

  // Check for code blocks (markdown or traditional)
  if (safeIncludes(safeText, '```') || safeMatch(safeText, /\`{3}[a-z]*\n[\s\S]*?\`{3}/)) {
    return 'code';
  }
  // Check for menu data
  else if (safeIncludes(safeText, 'Breakfast:') && safeIncludes(safeText, 'Lunch:') && safeIncludes(safeText, 'Dinner:')) {
    return 'menu';
  }
  // This is the key enhancement to better detect event formats like in the screenshot
  else if (
    // Detect events section headings
    (safeMatch(safeText, /events happening/i) || safeMatch(safeText, /upcoming events/i)) ||
    // Detect starred/bold event titles with time/location patterns
    (safeMatch(safeText, /\*\*[^*]+\*\*/i) &&
     (safeMatch(safeText, /\*\*Time:\*\*/i) || safeMatch(safeText, /\*\*Location:\*\*/i) ||
      safeMatch(safeText, /\*\*Date:\*\*/i) || safeMatch(safeText, /\*\*Description:\*\*/i))) ||
    // Traditional event detection
    ((safeIncludes(safeText, 'event') && (safeIncludes(safeText, 'date:') || safeIncludes(safeText, 'time:'))) ||
     safeMatch(safeText, /Events(\s+for|\s+on|\s+at)?\s+[A-Za-z]+,\s+[A-Za-z]+\s+\d{1,2}/i))
  ) {
    return 'event';
  }
  // Check for location data
  else if (safeIncludes(safeText, 'located at') || safeIncludes(safeText, 'location:') || safeIncludes(safeText, 'address:')) {
    return 'location';
  }
  // Check for schedule data (time patterns)
  else if (safeMatch(safeText, /\d{1,2}:\d{2}\s*(AM|PM|am|pm)/i) || safeMatch(safeText, /\d{1,2}\s*(AM|PM|am|pm)/i)) {
    return 'schedule';
  }
  // Check for resource lists
  else if (safeIncludes(safeText, 'Here are some resources') || safeIncludes(safeText, 'Here are resources')) {
    return 'resources';
  }
  // Check for steps/instructions (numbered lists or step mentions)
  else if (safeIncludes(safeText, 'steps') || safeMatch(safeText, /\d+\.\s+[\w\s]/) || safeMatch(safeText, /\*\s+Step\s+\d+:/i)) {
    return 'steps';
  }
  // Check for table data (markdown tables)
  else if (safeIncludes(safeText, '| --- |') || safeMatch(safeText, /\|[^|]+\|[^|]+\|/)) {
    return 'table';
  }
  // Default to general content
  return 'general';
};

/**
 * Function to extract go links from text
 * @param {any} text - The text to extract go links from
 * @returns {Array} Array of go link objects
 */
export const extractGoLinks = (text) => {
  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return [];

  // Match both go/linkname and go.middlebury.edu/linkname patterns
  const goLinkRegex = /(?:go\/(\w+)|go\.middlebury\.edu\/(\w+))/g;
  const links = [];
  let match;

  try {
    while ((match = goLinkRegex.exec(safeText)) !== null) {
      const name = ensureString(match[1] || match[2]);
      links.push({
        name: name,
        fullMatch: ensureString(match[0]),
        url: `https://go.middlebury.edu/${name}`
      });  // Missing closing parenthesis was here
    }

    return links;
  } catch (error) {
    console.error('Error extracting go links:', error);
  }

  return links;
}

/**
 * Function to parse menu data from text
 * @param {any} text - The text to parse menu data from
 * @returns {Object} Structured menu data
 */
export const parseMenuData = (text) => {
  // Default return object
  const defaultMenuData = {
    diningHall: "Dining Hall",
    meals: { breakfast: [], lunch: [], dinner: [] },
    specialDiets: { vegetarian: [], vegan: [], glutenFree: [] }
  };

  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return defaultMenuData;

  const menuData = {...defaultMenuData};

  try {
    // Extract dining hall name
    const diningHallMatch = safeMatch(safeText, /(?:at|in)\s+([A-Za-z]+(?:\s+[A-Za-z]+)?)\s+(?:dining\s+hall|cafeteria|commons)/i);
    if (diningHallMatch && diningHallMatch[1]) {
      menuData.diningHall = ensureString(diningHallMatch[1]);
    }

    // Extract meal items, supporting both text and markdown formats
    let currentMeal = null;
    let currentDiet = null;

    const lines = safeText.split('\n').map(line => ensureString(line));
    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Check for meal headers (both markdown and regular text)
      if (safeMatch(trimmedLine, /^#+\s*Breakfast/i) || safeMatch(trimmedLine, /Breakfast\s*:/i)) {
        currentMeal = 'breakfast';
        currentDiet = null;
      } else if (safeMatch(trimmedLine, /^#+\s*Lunch/i) || safeMatch(trimmedLine, /Lunch\s*:/i)) {
        currentMeal = 'lunch';
        currentDiet = null;
      } else if (safeMatch(trimmedLine, /^#+\s*Dinner/i) || safeMatch(trimmedLine, /Dinner\s*:/i)) {
        currentMeal = 'dinner';
        currentDiet = null;
      }
      // Check for dietary restriction sections
      else if (safeMatch(trimmedLine, /vegetarian/i)) {
        currentDiet = 'vegetarian';
      } else if (safeMatch(trimmedLine, /vegan/i)) {
        currentDiet = 'vegan';
      } else if (safeMatch(trimmedLine, /gluten.free/i)) {
        currentDiet = 'glutenFree';
      }
      // Process list items (both markdown and regular bullets)
      else if (currentMeal && trimmedLine &&
              (trimmedLine.startsWith('-') || trimmedLine.startsWith('*') || trimmedLine.startsWith('•'))) {
        // Extract the item text
        const itemText = ensureString(trimmedLine.substring(1).trim());

        // Add to appropriate collection based on context
        if (currentDiet && menuData.specialDiets[currentDiet]) {
          menuData.specialDiets[currentDiet].push(itemText);
        } else if (menuData.meals[currentMeal]) {
          menuData.meals[currentMeal].push(itemText);
        }
      }
    });
  } catch (error) {
    console.error('Error parsing menu data:', error);
  }

  return menuData;
};

/**
 * Function to parse location data from text
 * @param {any} text - The text to parse location data from
 * @returns {Object} Structured location data
 */
export const parseLocationData = (text) => {
  // Default return object
  const defaultLocationData = {
    name: "",
    address: "",
    hours: "",
    description: "",
    features: [],
    coordinates: null
  };

  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return defaultLocationData;

  const locationData = {...defaultLocationData};

  try {
    // Extract location name (from both markdown and regular formats)
    const nameMatch = safeMatch(safeText, /(?:^#+\s*([A-Za-z\s]+)|The\s+([A-Za-z\s]+)\s+is\s+located)/i);
    if (nameMatch) {
      locationData.name = ensureString(nameMatch[1] || nameMatch[2]).trim();
    }

    // Extract address
    const addressMatch = safeMatch(safeText, /(?:located\s+at|address\s*:|location\s*:)\s+([^\.]+)/i);
    if (addressMatch && addressMatch[1]) {
      locationData.address = ensureString(addressMatch[1]).trim();
    }

    // Extract hours if present
    const hoursMatch = safeMatch(safeText, /(?:hours\s*:|open\s+hours\s*:|open\s+)([^\.]+)(?:daily|weekdays|on weekdays|monday|tuesday|weekends)/i);
    if (hoursMatch && hoursMatch[1]) {
      locationData.hours = ensureString(hoursMatch[1]).trim();
    }

    // Extract coordinates if present (latitude, longitude format)
    const coordMatch = safeMatch(safeText, /(\d+\.\d+),\s*(\d+\.\d+)/);
    if (coordMatch && coordMatch[1] && coordMatch[2]) {
      try {
        locationData.coordinates = {
          latitude: parseFloat(coordMatch[1]),
          longitude: parseFloat(coordMatch[2])
        };
        // In case parsing produces NaN or invalid values
        if (isNaN(locationData.coordinates.latitude) || isNaN(locationData.coordinates.longitude)) {
          locationData.coordinates = null;
        }
      } catch (e) {
        locationData.coordinates = null;
      }
    }

    // Extract features (bullet points or markdown list items)
    const lines = safeText.split('\n').map(line => ensureString(line));
    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
        locationData.features.push(ensureString(trimmedLine.substring(1).trim()));
      }
    });

    // Extract description (first paragraph that's not a header)
    const paragraphs = safeText.split('\n\n').map(para => ensureString(para));
    for (const paragraph of paragraphs) {
      const trimmed = paragraph.trim();
      if (trimmed && !trimmed.startsWith('#') && !safeMatch(trimmed, /^[A-Za-z\s]+:/)) {
        locationData.description = trimmed;
        break;
      }
    }
  } catch (error) {
    console.error('Error parsing location data:', error);
  }

  return locationData;
};

/**
 * Function to parse schedule data from text
 * @param {any} text - The text to parse schedule data from
 * @returns {Array} Array of schedule items
 */
export const parseScheduleData = (text) => {
  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return [];

  const scheduleItems = [];
  let currentSection = null;

  try {
    const lines = safeText.split('\n').map(line => ensureString(line));
    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Handle markdown headers and section headings
      const headerMatch = safeMatch(trimmedLine, /^#+\s+(.+)$/);
      if (headerMatch && headerMatch[1]) {
        currentSection = ensureString(headerMatch[1]);
      } else if (trimmedLine.endsWith(':') && !safeMatch(trimmedLine, /\d+:\d+/)) {
        currentSection = ensureString(trimmedLine.slice(0, -1));
      }
      // Handle list items (markdown and regular bullets)
      else if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
        // Extract the item text
        const item = ensureString(trimmedLine.substring(1).trim());

        // Look for time patterns
        const timeMatch = safeMatch(item, /(\d{1,2}:\d{2}\s*(AM|PM|am|pm)|\d{1,2}\s*(AM|PM|am|pm))/i);

        if (timeMatch && timeMatch[0]) {
          const time = ensureString(timeMatch[0]);
          const description = ensureString(item.replace(time, '').trim() || currentSection);

          scheduleItems.push({
            time: time,
            description: description,
            section: ensureString(currentSection)
          });
        }
      }
    });

    // Sort by time
    scheduleItems.sort((a, b) => {
      return convertTimeToMinutes(a.time) - convertTimeToMinutes(b.time);
    });
  } catch (error) {
    console.error('Error parsing schedule data:', error);
  }

  return scheduleItems;
};

/**
 * Helper function to convert time to minutes for sorting
 * @param {any} timeStr - Time string (format: "1:30 PM" or "1 PM")
 * @returns {number} Minutes since midnight
 */
const convertTimeToMinutes = (timeStr) => {
  const safeTimeStr = ensureString(timeStr);
  if (!safeTimeStr) return 0;

  try {
    const isPM = safeTimeStr.toLowerCase().includes('pm');
    let hours = 0, minutes = 0;

    if (safeTimeStr.includes(':')) {
      const parts = safeTimeStr.split(':').map(part => ensureString(part));
      hours = parseInt(parts[0], 10) || 0;
      // Extract numbers from the minutes part (which might include "AM" or "PM")
      minutes = parseInt((parts[1] || '').replace(/[^\d]/g, ''), 10) || 0;
    } else {
      // Extract numbers from the string
      const hoursMatch = safeMatch(safeTimeStr, /\d+/);
      hours = hoursMatch ? parseInt(hoursMatch[0], 10) : 0;
      minutes = 0;
    }

    if (isPM && hours < 12) hours += 12;
    if (!isPM && hours === 12) hours = 0;

    return hours * 60 + minutes;
  } catch (error) {
    console.error('Error converting time to minutes:', error);
    return 0;
  }
};

/**
 * Function to parse steps data from text
 * @param {any} text - The text to parse steps data from
 * @returns {Object} Steps data with title and steps array
 */
export const parseStepsData = (text) => {
  // Default return object
  const defaultStepsData = { title: "", steps: [] };

  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return defaultStepsData;

  const result = {...defaultStepsData};

  try {
    // Extract title (first line or first markdown header)
    const lines = safeText.split('\n').map(line => ensureString(line));
    if (lines.length === 0) return result;

    const firstLine = ensureString(lines[0]);
    const titleMatch = safeMatch(firstLine, /^#+\s+(.+)$/);

    if (titleMatch && titleMatch[1]) {
      result.title = ensureString(titleMatch[1]);
    } else if (!safeMatch(firstLine, /^\d+\./)) {
      result.title = firstLine.trim();
    }

    // Extract steps (numbered list items)
    let stepNumber = 1;

    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Match numbered list items (both markdown and regular)
      const numberedStepMatch = safeMatch(trimmedLine, /^\d+\.\s+(.+)/);
      if (numberedStepMatch && numberedStepMatch[1]) {
        result.steps.push({
          number: stepNumber++,
          text: ensureString(numberedStepMatch[1])
        });
      }
      // Match markdown list items that might be steps
      else if ((trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) &&
               (trimmedLine.toLowerCase().includes('step') || result.steps.length > 0)) {
        result.steps.push({
          number: stepNumber++,
          text: ensureString(trimmedLine.substring(2))
        });
      }
    });
  } catch (error) {
    console.error('Error parsing steps data:', error);
  }

  return result;
};

/**
 * Function to parse resources data from text
 * @param {any} text - The text to parse resources data from
 * @returns {Array} Array of resource objects
 */
export const parseResourcesData = (text) => {
  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return [];

  const resources = [];
  let currentName = null;
  let currentUrl = null;
  let currentDescription = null;

  try {
    // Handle markdown links and bullet points
    const lines = safeText.split('\n').map(line => ensureString(line));
    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Match markdown links [name](url)
      const markdownLinkMatch = safeMatch(trimmedLine, /\[(.*?)\]\((https?:\/\/[^\s)]+)\)/);
      if (markdownLinkMatch && markdownLinkMatch[1] && markdownLinkMatch[2]) {
        // If we already have an in-progress resource, save it
        if (currentName) {
          resources.push({
            name: ensureString(currentName),
            url: currentUrl ? ensureString(currentUrl) : generateGoLink(currentName),
            description: ensureString(currentDescription || '')
          });
        }

        // Start a new resource from markdown link
        currentName = ensureString(markdownLinkMatch[1]);
        currentUrl = ensureString(markdownLinkMatch[2]);
        currentDescription = ensureString(trimmedLine.replace(markdownLinkMatch[0], '').trim());

        // If there's no additional description, check the next line
        if (!currentDescription) {
          currentDescription = null;
        }
      }
      // Handle bullet points and list items
      else if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
        // If we already have an in-progress resource, save it
        if (currentName) {
          resources.push({
            name: ensureString(currentName),
            url: currentUrl ? ensureString(currentUrl) : generateGoLink(currentName),
            description: ensureString(currentDescription || '')
          });
        }

        // Start a new resource
        const item = ensureString(trimmedLine.substring(1).trim());

        // Check for URL pattern (parentheses)
        const urlMatch = safeMatch(item, /\(([^)]+)\)/);
        if (urlMatch && urlMatch[1]) {
          currentUrl = ensureString(urlMatch[1]);
          currentName = ensureString(item.replace(/\([^)]+\)/, '').trim().replace(':', ''));
        } else {
          currentName = ensureString(item.replace(':', ''));
          currentUrl = null;
        }

        currentDescription = null;
      }
      // Handle indented descriptions
      else if (currentName && trimmedLine && !trimmedLine.endsWith(':') &&
              (line.startsWith('  ') || line.startsWith('\t'))) {
        currentDescription = ensureString(trimmedLine);
      }
    });

    // Add the last resource if there is one
    if (currentName) {
      resources.push({
        name: ensureString(currentName),
        url: currentUrl ? ensureString(currentUrl) : generateGoLink(currentName),
        description: ensureString(currentDescription || '')
      });
    }
  } catch (error) {
    console.error('Error parsing resources data:', error);
  }

  return resources;
};

/**
 * Generate a go link URL from a resource name
 * @param {any} name - Resource name
 * @returns {string} Go link URL
 */
const generateGoLink = (name) => {
  const safeName = ensureString(name);
  if (!safeName) return 'https://go.middlebury.edu/resources';

  try {
    return `https://go.middlebury.edu/${safeName.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '')}`;
  } catch (error) {
    console.error('Error generating go link:', error);
    return 'https://go.middlebury.edu/resources';
  }
};

/**
 * Function to parse event data from text
 * @param {any} text - The text to parse event data from
 * @returns {Array} Array of event objects
 */
/**
 * Enhanced function to parse event data from text, handling markdown formatting
 * @param {any} text - The text to parse event data from
 * @returns {Array} Array of event objects
 */
export const parseEventData = (text) => {
  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return [];

  const events = [];
  let currentEvent = null;

  try {
    // Split by lines and process each
    const lines = safeText.split('\n').map(line => ensureString(line));
    lines.forEach(line => {
      const trimmedLine = line.trim();

      // Skip empty lines
      if (!trimmedLine) return;

      // Check for event titles (markdown headers or event names ending with colon)
      // Also handle markdown bold formatting with ** or __
      const headerMatch = safeMatch(trimmedLine, /^#+\s+(.+)$/);
      const boldMatch = safeMatch(trimmedLine, /^\*\*([^*:]+)(?:\*\*)?(?::)?$/);
      const underscoreMatch = safeMatch(trimmedLine, /^__([^_:]+)(?:__)?(?::)?$/);

      if (headerMatch ||
          boldMatch ||
          underscoreMatch ||
          (trimmedLine.endsWith(':') &&
           !safeIncludes(trimmedLine.toLowerCase(), 'time:') &&
           !safeIncludes(trimmedLine.toLowerCase(), 'date:') &&
           !safeIncludes(trimmedLine.toLowerCase(), 'description:') &&
           !safeIncludes(trimmedLine.toLowerCase(), 'location:'))) {

        // Save previous event if it exists
        if (currentEvent) {
          events.push(currentEvent);
        }

        // Get the title, handling various formats
        let title = '';
        if (headerMatch) {
          title = ensureString(headerMatch[1]);
        } else if (boldMatch) {
          title = ensureString(boldMatch[1]);
        } else if (underscoreMatch) {
          title = ensureString(underscoreMatch[1]);
        } else {
          title = ensureString(trimmedLine.replace(/^#+\s+/, '').replace(/:$/, ''));
        }

        // Clean title of any remaining markdown
        title = title.replace(/\*\*/g, '').replace(/__/g, '');

        // Start new event
        currentEvent = {
          title: title,
          date: null,
          time: null,
          location: null,
          description: null,
          category: null
        };
      }
      // Extract event details if we have an active event
      else if (currentEvent) {
        // Handle markdown formatted fields with ** or __
        const timeBoldMatch = safeMatch(trimmedLine, /\*\*Time:\*\*\s*(.+)/i);
        const timeUnderscoreMatch = safeMatch(trimmedLine, /__Time:__\s*(.+)/i);
        const dateBoldMatch = safeMatch(trimmedLine, /\*\*Date:\*\*\s*(.+)/i);
        const dateUnderscoreMatch = safeMatch(trimmedLine, /__Date:__\s*(.+)/i);
        const locationBoldMatch = safeMatch(trimmedLine, /\*\*Location:\*\*\s*(.+)/i);
        const locationUnderscoreMatch = safeMatch(trimmedLine, /__Location:__\s*(.+)/i);
        const descriptionBoldMatch = safeMatch(trimmedLine, /\*\*Description:\*\*\s*(.+)/i);
        const descriptionUnderscoreMatch = safeMatch(trimmedLine, /__Description:__\s*(.+)/i);

        // Parse time
        if (timeBoldMatch || timeUnderscoreMatch || safeIncludes(trimmedLine.toLowerCase(), 'time:')) {
          if (timeBoldMatch) {
            currentEvent.time = ensureString(timeBoldMatch[1]).trim();
          } else if (timeUnderscoreMatch) {
            currentEvent.time = ensureString(timeUnderscoreMatch[1]).trim();
          } else {
            const parts = trimmedLine.split(':');
            if (parts.length > 2) {
              // Get everything after the first colon
              currentEvent.time = ensureString(trimmedLine.substring(trimmedLine.indexOf(':') + 1)).trim();
            } else if (parts.length > 1) {
              currentEvent.time = ensureString(parts[1]).trim();
            }
          }
          // Clean up any remaining markdown characters
          if (currentEvent.time) {
            currentEvent.time = currentEvent.time.replace(/\*\*/g, '').replace(/__/g, '');
          }
        }

        // Parse date
        else if (dateBoldMatch || dateUnderscoreMatch || safeIncludes(trimmedLine.toLowerCase(), 'date:')) {
          if (dateBoldMatch) {
            currentEvent.date = ensureString(dateBoldMatch[1]).trim();
          } else if (dateUnderscoreMatch) {
            currentEvent.date = ensureString(dateUnderscoreMatch[1]).trim();
          } else {
            const parts = trimmedLine.split(':');
            if (parts.length > 1) {
              currentEvent.date = ensureString(parts[1]).trim();
            }
          }
          // Clean up any remaining markdown characters
          if (currentEvent.date) {
            currentEvent.date = currentEvent.date.replace(/\*\*/g, '').replace(/__/g, '');
          }
        }

        // Parse location
        else if (locationBoldMatch || locationUnderscoreMatch || safeIncludes(trimmedLine.toLowerCase(), 'location:')) {
          if (locationBoldMatch) {
            currentEvent.location = ensureString(locationBoldMatch[1]).trim();
          } else if (locationUnderscoreMatch) {
            currentEvent.location = ensureString(locationUnderscoreMatch[1]).trim();
          } else {
            const parts = trimmedLine.split(':');
            if (parts.length > 1) {
              currentEvent.location = ensureString(parts[1]).trim();
            }
          }
          // Clean up any remaining markdown characters
          if (currentEvent.location) {
            currentEvent.location = currentEvent.location.replace(/\*\*/g, '').replace(/__/g, '');
          }
        }

        // Parse description
        else if (descriptionBoldMatch || descriptionUnderscoreMatch || safeIncludes(trimmedLine.toLowerCase(), 'description:')) {
          if (descriptionBoldMatch) {
            currentEvent.description = ensureString(descriptionBoldMatch[1]).trim();
          } else if (descriptionUnderscoreMatch) {
            currentEvent.description = ensureString(descriptionUnderscoreMatch[1]).trim();
          } else {
            const parts = trimmedLine.split(':');
            if (parts.length > 1) {
              currentEvent.description = ensureString(parts.slice(1).join(':').trim());
            }
          }
          // Clean up any remaining markdown characters
          if (currentEvent.description) {
            currentEvent.description = currentEvent.description.replace(/\*\*/g, '').replace(/__/g, '');
          }
        }

        // Parse category
        else if (safeIncludes(trimmedLine.toLowerCase(), 'category:')) {
          const parts = trimmedLine.split(':');
          currentEvent.category = parts.length > 1 ? ensureString(parts[1]).trim() : '';
          // Clean up any remaining markdown characters
          if (currentEvent.category) {
            currentEvent.category = currentEvent.category.replace(/\*\*/g, '').replace(/__/g, '');
          }
        }

        // Handle markdown links for "More Info"
        else if (safeMatch(trimmedLine, /\[(More Info|Learn More|Details)\]\(([^)]+)\)/i)) {
          // Don't change the current event, but could store the URL if needed
          // Currently just skipping this line
        }

        // Everything else is considered description if no description yet
        else if (!currentEvent.description &&
                !trimmedLine.startsWith('[') &&
                !trimmedLine.startsWith('•') &&
                !trimmedLine.startsWith('-') &&
                !trimmedLine.startsWith('*')) {
          // Clean up any markdown formatting
          currentEvent.description = ensureString(trimmedLine).replace(/\*\*/g, '').replace(/__/g, '');
        }
        // Append to existing description
        else if (currentEvent.description &&
                !trimmedLine.startsWith('[') &&
                !trimmedLine.startsWith('•') &&
                !trimmedLine.startsWith('-') &&
                !trimmedLine.startsWith('*')) {
          const cleanedLine = ensureString(trimmedLine).replace(/\*\*/g, '').replace(/__/g, '');
          currentEvent.description = ensureString(currentEvent.description) + '\n' + cleanedLine;
        }
      }
    });

    // Add the last event if it exists
    if (currentEvent) {
      events.push(currentEvent);
    }
  } catch (error) {
    console.error('Error parsing event data:', error);
  }

  return events;
};

/**
 * Function to parse markdown table data
 * @param {any} text - The text to parse table data from
 * @returns {Object} Table data with headers and rows
 */
export const parseTableData = (text) => {
  // Default return object
  const defaultTableData = { headers: [], rows: [] };

  // Ensure text is a string
  const safeText = ensureString(text);
  if (!safeText) return defaultTableData;

  const tableData = {...defaultTableData};

  try {
    const lines = safeText.split('\n')
      .map(line => ensureString(line))
      .filter(line => line.trim());

    let inTable = false;

    for (let i = 0; i < lines.length; i++) {
      const line = ensureString(lines[i]).trim();

      // Detect the start of a table (first row with pipe delimiters)
      if (!inTable && safeIncludes(line, '|')) {
        inTable = true;

        // Parse headers from first row
        tableData.headers = line
          .split('|')
          .map(cell => ensureString(cell).trim())
          .filter(cell => cell !== '');

        // Skip the separator row if it exists
        if (i + 1 < lines.length && safeIncludes(lines[i + 1], '---')) {
          i++;
        }
      }
      // Process table rows
      else if (inTable && safeIncludes(line, '|') && !safeIncludes(line, '---')) {
        const cells = line
          .split('|')
          .map(cell => ensureString(cell).trim())
          .filter(cell => cell !== '');

        tableData.rows.push(cells);
      }
      // Detect end of table
      else if (inTable && !safeIncludes(line, '|')) {
        inTable = false;
      }
    }
  } catch (error) {
    console.error('Error parsing table data:', error);
  }

  return tableData;
};

/**
 * Function to parse general text content into sections
 * @param {any} content - The content to parse
 * @returns {Array} Array of section objects
 */
/**
 * Main function to parse any content based on its detected type
 * @param {any} content - The content to parse
 * @returns {Object} Parsed content with type and data
 */
export const parseContent = (content) => {
  // Ensure content is a string
  const safeContent = ensureString(content);
  if (!safeContent) {
    return {
      type: 'general',
      data: [],
      goLinks: []
    };
  }

  const contentType = detectContentType(safeContent);
  let parsedData;

  try {
    switch (contentType) {
      case 'menu':
        parsedData = parseMenuData(safeContent);
        break;
      case 'location':
        parsedData = parseLocationData(safeContent);
        break;
      case 'schedule':
        parsedData = parseScheduleData(safeContent);
        break;
      case 'steps':
        parsedData = parseStepsData(safeContent);
        break;
      case 'resources':
        parsedData = parseResourcesData(safeContent);
        break;
      case 'event':
        parsedData = parseEventData(safeContent);
        break;
      case 'table':
        parsedData = parseTableData(safeContent);
        break;
      case 'code':
        // For code, keep the raw content and extract language if present
        const codeMatch = safeMatch(safeContent, /\`{3}([a-zA-Z]*)\n([\s\S]*?)\`{3}/);
        parsedData = {
          language: codeMatch && codeMatch[1] ? ensureString(codeMatch[1]) : null,
          code: codeMatch && codeMatch[2] ? ensureString(codeMatch[2]) : safeContent
        };
        break;
      case 'general':
      default:
        parsedData = parseGeneralContent(safeContent);
    }
  } catch (error) {
    console.error('Error parsing content:', error);
    parsedData = { error: ensureString(error.message || 'Unknown parsing error') };
  }

  return {
    type: ensureString(contentType),
    data: parsedData || {},
    goLinks: extractGoLinks(safeContent)
  };
};

/**
 * Function to parse general text content into sections
 * @param {any} content - The content to parse
 * @returns {Array} Array of section objects
 */
export const parseGeneralContent = (content) => {
  // Ensure content is a string
  const safeContent = ensureString(content);
  if (!safeContent) return [];

  const sections = [];
  let currentSection = { title: null, items: [] };

  try {
    const lines = safeContent.split('\n').map(line => ensureString(line));

    lines.forEach(line => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      // Check for markdown headers as section titles
      const headerMatch = safeMatch(trimmedLine, /^#+\s+(.+)$/);
      if (headerMatch && headerMatch[1]) {
        // Save previous section if it has items
        if (currentSection.items.length > 0) {
          sections.push({...currentSection});
        }

        // Start new section with markdown header as title
        currentSection = {
          title: ensureString(headerMatch[1]),
          items: []
        };
      }
      // Check for section headers ending with colon
      else if (trimmedLine.endsWith(':') && !safeIncludes(trimmedLine, 'http')) {
        // Save previous section if it has items
        if (currentSection.items.length > 0) {
          sections.push({...currentSection});
        }

        // Start new section
        currentSection = {
          title: ensureString(trimmedLine.slice(0, -1)), // Remove the colon
          items: []
        };
      }
      // If it's a bullet point or markdown list item or numbered item, it's a list item
      else if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') ||
               trimmedLine.startsWith('*') || safeMatch(trimmedLine, /^\d+\./)) {
        let content = trimmedLine;

        // Remove list markers
        if (trimmedLine.startsWith('•') || trimmedLine.startsWith('-') || trimmedLine.startsWith('*')) {
          content = ensureString(trimmedLine.substring(1).trim());
        } else if (safeMatch(trimmedLine, /^\d+\./)) {
          const match = safeMatch(trimmedLine, /^\d+\.\s+(.+)/);
          content = match && match[1] ? ensureString(match[1]) : ensureString(trimmedLine);
        }

        currentSection.items.push({
          type: 'list-item',
          content: content
        });
      }
      // If it looks like a paragraph
      else if (trimmedLine.length > 20 || trimmedLine.endsWith('.')) {
        currentSection.items.push({
          type: 'paragraph',
          content: ensureString(trimmedLine)
        });
      }
      // Otherwise, if we have a title, add to current section's items
      else if (currentSection.title) {
        currentSection.items.push({
          type: 'text',
          content: ensureString(trimmedLine)
        });
      }
// If no title yet, this line might be the intro text
      else {
        // Check if this line could be an unlabeled title
        if (sections.length === 0) {
          currentSection.title = 'Introduction';
        }
        currentSection.items.push({
          type: 'text',
          content: ensureString(trimmedLine)
        });
      }
    });

    // Add the last section if it has items
    if (currentSection.items.length > 0) {
      sections.push({...currentSection});
    }

    return sections;
  } catch (error) {
    console.error('Error parsing general content:', error);
    return [];
  }
};